import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAudits } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAudits().then(res => setAudits(res.data.audits || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const completed = audits.filter(a => a.status === 'completed');
  const avgScore = completed.length > 0
    ? Math.round(completed.reduce((s, a) => s + (a.compliance_score || 0), 0) / completed.length)
    : '--';
  const totalCritical = completed.reduce((s, a) => s + (a.critical_count || 0), 0);
  const totalWarnings = completed.reduce((s, a) => s + (a.warning_count || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Loading...</p></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Welcome back, {user?.firstName}</h1>
        <p className="text-gray-500 mt-1">{user?.agencyName} — Audit Dashboard</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Total Audits</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{audits.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Average Score</p>
          <p className="text-3xl font-bold text-blue-600 mt-1">{avgScore}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Critical Issues Found</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{totalCritical}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500">Warnings Found</p>
          <p className="text-3xl font-bold text-yellow-600 mt-1">{totalWarnings}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4 mb-8">
        <Link to="/upload"
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition">
          Upload & Audit New Chart
        </Link>
        <Link to="/audits"
          className="bg-white hover:bg-gray-50 text-slate-700 px-6 py-3 rounded-lg font-semibold border border-gray-200 transition">
          View All Audits
        </Link>
      </div>

      {/* Recent Audits */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-slate-800">Recent Audits</h2>
        </div>
        {audits.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <p className="text-lg">No audits yet</p>
            <p className="mt-2">Upload a clinical chart to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {audits.slice(0, 10).map((audit) => (
              <Link key={audit.id} to={`/audits/${audit.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition">
                <div>
                  <p className="font-medium text-slate-800">{audit.original_filename}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(audit.created_at).toLocaleDateString()} — {audit.initiated_by_name}
                    {audit.patient_reference && ` — Patient: ${audit.patient_reference}`}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  {audit.status === 'completed' ? (
                    <>
                      <div className="flex space-x-2 text-sm">
                        {audit.critical_count > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{audit.critical_count} critical</span>}
                        {audit.warning_count > 0 && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">{audit.warning_count} warnings</span>}
                        {audit.info_count > 0 && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{audit.info_count} info</span>}
                      </div>
                      <span className={`text-2xl font-bold ${audit.compliance_score >= 80 ? 'text-green-600' : audit.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {audit.compliance_score}
                      </span>
                    </>
                  ) : audit.status === 'processing' ? (
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm animate-pulse">Processing...</span>
                  ) : (
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">Failed</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
